// "Gnosis Featured" — the form behind the Promote card on the Spotlight rail.
//
// One product, not two. Promoting a Gnosis profile and promoting an external
// site were the same thing wearing two labels, so this is a single form with
// an optional link rather than a choice between tiers.
//
// Submitting charges nothing. The admin's approval is what swaps the
// subscription price to the Promotion price, so this is a submission and the
// copy says so — no checkout, no redirect, nothing to refund if it is turned
// down.
import { createPortal } from 'react-dom';
import { useMemo, useState } from 'react';
import { Rocket, UserRound, X } from 'lucide-react';
import { Face } from './Spotlights';
import { creatorName } from '../utils/creators';
import { useTechniques } from '../hooks/useTechniques';
import { useRankedCreators } from '../hooks/useRankedCreators';
import { PROMO_PLATFORMS, PROMO_PRICE, createPromotionSetupSession, submitPromotion, type PromoPlatform, type Promotion } from '../api/promotions';

/** The rail, three cells wide, with their card in the slot they would buy.
 *  Built from the same classes Home uses, so this is the actual thing rather
 *  than a drawing of it — and the two creators beside them are real ones out
 *  of the catalog, not placeholders. */
function RailPreview() {
  const { techniques } = useTechniques();
  const ranked = useRankedCreators(techniques);
  // Padded to two: the point of the preview is the position, so it has to
  // show cards to the right of theirs even before the catalog has loaded.
  const neighbours = useMemo(() => {
    const handles = ranked.slice(0, 2).map(([handle]) => handle);
    return [handles[0] ?? null, handles[1] ?? null];
  }, [ranked]);

  return (
    <div className="promote-preview">
      <div className="promote-preview-rail">
        <div className="rail-creator">
          <span className="rail-promo-face">
            <span className="rail-face rail-face--none promote-preview-you">
              <span className="promote-preview-you-inner">
                <UserRound size={30} strokeWidth={1.6} />
              </span>
            </span>
          </span>
          <span className="rail-creator-name rail-creator-name--gold">You</span>
        </div>
        {neighbours.map((handle, i) => (
          <div className="rail-creator promote-preview-dim" key={handle ?? i}>
            {handle
              ? <Face className="rail-face" handle={handle} />
              : <span className="rail-face rail-face--none" />}
            <span className="rail-creator-name">{handle ? creatorName(handle) : 'A creator'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PromoteModal({ existing, onClose, onDone }: {
  /** Their current Promotion, if they have one — this doubles as the edit form. */
  existing?: Promotion | null;
  onClose: () => void;
  onDone: () => void;
}) {
  // An edit starts from whatever is staged, else from what is live.
  const seed = existing?.pending ?? existing ?? null;
  const [handle, setHandle] = useState(seed?.handle ?? '');
  // Records written before the other platforms existed are all Instagram.
  const [platform, setPlatform] = useState<PromoPlatform>(seed?.platform ?? 'instagram');
  const [name, setName] = useState(seed?.name ?? '');
  const [blurb, setBlurb] = useState(seed?.blurb ?? '');
  const [url, setUrl] = useState(seed?.url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!existing && existing.status !== 'rejected';
  // The pitch comes first; the form only appears once they have said yes to
  // it. An edit skips straight to the fields — they have already bought.
  const [showForm, setShowForm] = useState(isEdit);

  // Closing throws the session away: reopening starts at the pitch again, not
  // half-way down a form they walked out of.
  const close = () => {
    setShowForm(isEdit);
    setHandle(seed?.handle ?? '');
    setPlatform(seed?.platform ?? 'instagram');
    setName(seed?.name ?? '');
    setBlurb(seed?.blurb ?? '');
    setUrl(seed?.url ?? '');
    setError('');
    onClose();
  };
  const ready = handle.trim() && name.trim() && blurb.trim();

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      const { needsCard } = await submitPromotion({ handle: handle.trim(), platform, name: name.trim(), blurb: blurb.trim(), url: url.trim() || undefined });
      // A gift-code buyer has no subscription to bill later, so they save a
      // card now. Stripe's setup mode does not charge it — approval is still
      // the only thing that takes money.
      if (needsCard) {
        window.location.href = await createPromotionSetupSession();
        return;
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit that. Try again.');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="frame-modal-backdrop" onClick={close}>
      <div className="gift-modal promote-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={close} aria-label="Close">
          <X size={18} />
        </button>

        <h2 className="promote-title">
          <Rocket size={18} className="promote-gem" />
          {isEdit ? 'Edit your Featured card' : 'Always appear in Spotlight'}
        </h2>
        {/* The pitch. Once they are filling the form they have taken it, and
            repeating it just crowds the fields. */}
        {!showForm && (
          <p className="promote-sub">
            Increase visibility for yourself or your product for ${PROMO_PRICE}/mo.
          </p>
        )}

        {!showForm ? (
          <>
            <RailPreview />
            <button type="button" className="promote-submit" onClick={() => setShowForm(true)}>
              Upgrade Now
            </button>
          </>
        ) : (
          <>
        {/* The handle is not always an Instagram one, and it decides both the
            link on their card and where the portrait is fetched from, so they
            pick the site rather than us assuming it. */}
        <label className="promote-field">
          <span>Your handle</span>
          <div className="promote-handle-row">
            <select
              className="promote-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as PromoPlatform)}
              aria-label="Platform"
            >
              {PROMO_PLATFORMS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="yourhandle" maxLength={40} autoCapitalize="none" />
          </div>
        </label>
        <label className="promote-field">
          <span>Display name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={60} />
        </label>
        <label className="promote-field">
          <span>One line about your product</span>
          <input value={blurb} onChange={(e) => setBlurb(e.target.value)} placeholder="What it is, in a few words" maxLength={120} />
        </label>
        <label className="promote-field">
          <span>Link out to <em>(optional)</em></span>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yoursite.com" maxLength={300} autoCapitalize="none" />
        </label>

        {error && <p className="promote-error">{error}</p>}

        <button type="button" className="promote-submit" onClick={submit} disabled={!ready || busy}>
          {busy ? 'Sending…' : 'Submit for Review'}
        </button>
        <p className="promote-foot">
          {isEdit
            ? 'Your card keeps showing the approved version until this is reviewed.'
            : `Nothing is charged until it is approved. $${PROMO_PRICE}/month, your Gnosis membership included. Cancel any time in Settings.`}
        </p>
          </>
        )}
      </div>
    </div>,
    // Over the phone frame rather than the whole viewport, like every other
    // modal — a fixed backdrop on document.body escapes the frame on desktop.
    document.querySelector('.phone-frame') ?? document.body,
  );
}
